// src/modules/ambassadors/components/AmbassadorModal.tsx - Создание/редактирование амбассадора

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Radio,
  Spin,
  Alert,
  Button,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TagOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  AmbassadorProfile,
  CreateAmbassadorRequest,
  UpdateAmbassadorRequest,
} from '@/types/models/ambassador';
import {
  validatePromoCode,
  formatPromoCode,
  generateRandomPromoCode,
} from '../utils/ambassadorUtils';
import { ambassadorsApi } from '@/services/api/ambassadors';

const { Option } = Select;

interface AmbassadorModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAmbassadorRequest | UpdateAmbassadorRequest) => Promise<void>;
  ambassador?: AmbassadorProfile | null;
  mode: 'create' | 'edit';
}

export const AmbassadorModal: React.FC<AmbassadorModalProps> = ({
  visible,
  onClose,
  onSubmit,
  ambassador,
  mode,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [creationMode, setCreationMode] = useState<'existing' | 'new'>('existing');
  const [promoCodeError, setPromoCodeError] = useState<string>('');

  // Загрузка списка клиентов для выбора
  const loadCustomers = async (search?: string) => {
    setCustomersLoading(true);
    try {
      const response = await ambassadorsApi.getAvailableCustomers({
        search,
        per_page: 50,
      });
      setCustomers(response.data.data.results);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Инициализация формы
  useEffect(() => {
    if (visible) {
      if (mode === 'edit' && ambassador) {
        // Режим редактирования
        form.setFieldsValue({
          promo_code: ambassador.promo_code,
          is_active: ambassador.is_active,
        });
      } else {
        // Режим создания
        form.resetFields();
        setCreationMode('existing');
        loadCustomers();
      }
    }
  }, [visible, mode, ambassador]);

  // Обработка отправки формы
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (mode === 'create') {
        // Создание нового амбассадора
        const createData: CreateAmbassadorRequest = {
          promo_code: formatPromoCode(values.promo_code),
          is_active: values.is_active ?? true,
        };

        if (creationMode === 'existing') {
          createData.customer_id = values.customer_id;
        } else {
          createData.create_new_customer = {
            name: values.new_customer_name,
            phone: values.new_customer_phone,
            email: values.new_customer_email,
          };
        }

        await onSubmit(createData);
      } else {
        // Редактирование существующего
        const updateData: UpdateAmbassadorRequest = {
          promo_code: formatPromoCode(values.promo_code),
          is_active: values.is_active,
        };

        await onSubmit(updateData);
      }

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Валидация промокода при вводе
  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    form.setFieldValue('promo_code', value);

    const validation = validatePromoCode(value);
    setPromoCodeError(validation.error || '');
  };

  // Генерация случайного промокода
  const handleGeneratePromoCode = () => {
    const newCode = generateRandomPromoCode('AMB');
    form.setFieldValue('promo_code', newCode);
    setPromoCodeError('');
  };

  // Поиск клиентов при вводе
  const handleCustomerSearch = (value: string) => {
    if (value) {
      loadCustomers(value);
    }
  };

  return (
    <Modal
      title={mode === 'create' ? 'Создать амбассадора' : 'Редактировать амбассадора'}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        {mode === 'create' && (
          <>
            {/* Выбор режима создания */}
            <Form.Item label="Способ создания">
              <Radio.Group
                value={creationMode}
                onChange={(e) => setCreationMode(e.target.value)}
              >
                <Radio.Button value="existing">Выбрать клиента</Radio.Button>
                <Radio.Button value="new">Создать нового</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {/* Вариант 1: Выбор существующего клиента */}
            {creationMode === 'existing' && (
              <Form.Item
                name="customer_id"
                label="Клиент"
                rules={[{ required: true, message: 'Выберите клиента' }]}
              >
                <Select
                  showSearch
                  placeholder="Начните вводить имя или телефон"
                  filterOption={false}
                  onSearch={handleCustomerSearch}
                  loading={customersLoading}
                  notFoundContent={customersLoading ? <Spin size="small" /> : 'Клиенты не найдены'}
                  suffixIcon={<UserOutlined />}
                >
                  {customers.map((customer) => (
                    <Option
                      key={customer.id}
                      value={customer.id}
                      disabled={customer.is_ambassador}
                    >
                      <div>
                        <div>{customer.name}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {customer.phone}
                          {customer.is_ambassador && ' (уже амбассадор)'}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {/* Вариант 2: Создание нового клиента */}
            {creationMode === 'new' && (
              <>
                <Form.Item
                  name="new_customer_name"
                  label="Имя клиента"
                  rules={[
                    { required: true, message: 'Введите имя клиента' },
                    { min: 2, message: 'Минимум 2 символа' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Иван Иванов"
                  />
                </Form.Item>

                <Form.Item
                  name="new_customer_phone"
                  label="Телефон клиента"
                  rules={[
                    { required: true, message: 'Введите телефон клиента' },
                    {
                      validator: (_, value) => {
                        const cleaned = value.replace(/[^\d]/g, '');
                        if (cleaned.length !== 11) {
                          return Promise.reject('Номер должен содержать 11 цифр');
                        }
                        if (!cleaned.startsWith('7') && !cleaned.startsWith('8')) {
                          return Promise.reject('Номер должен начинаться с 7 или 8');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  normalize={(value) => value.replace(/[^\d]/g, '')}  // ✅ ДОБАВИТЬ: удаляем всё кроме цифр
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="7 (XXX) XXX-XX-XX"  // ✅ Убрали +
                    maxLength={11}  // ✅ ДОБАВИТЬ: ограничение
                  />
                </Form.Item>

                <Form.Item
                  name="new_customer_email"
                  label="Email клиента (необязательно)"
                  rules={[
                    { type: 'email', message: 'Неверный формат email' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="example@mail.com"
                  />
                </Form.Item>
              </>
            )}
          </>
        )}

        {/* Промокод (для создания и редактирования) */}
        <Form.Item
          name="promo_code"
          label="Промокод"
          rules={[
            { required: true, message: 'Введите промокод' },
            { min: 3, message: 'Минимум 3 символа' },
            { max: 50, message: 'Максимум 50 символов' },
          ]}
          validateStatus={promoCodeError ? 'error' : ''}
          help={promoCodeError || 'Только латинские буквы, цифры и подчеркивание'}
        >
          <Input
            prefix={<TagOutlined />}
            placeholder="PROMO_CODE"
            onChange={handlePromoCodeChange}
            suffix={
              mode === 'create' && (
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleGeneratePromoCode}
                >
                  Генерировать
                </Button>
              )
            }
          />
        </Form.Item>

        {/* Статус активности */}
        <Form.Item
          name="is_active"
          label="Активен"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch />
        </Form.Item>

        {/* Предупреждение для режима редактирования */}
        {mode === 'edit' && (
          <Alert
            message="Внимание"
            description="Изменение промокода может повлиять на существующие реферальные ссылки."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
};